'use client';

/**
 * CallToolTimeoutPanel — the voice-call surface's variant of the
 * tool-timeout recovery flow (design section 20b). Pure presentational
 * switch on `flow.state.view` — same shape as tool-timeout-card.tsx (text
 * chat's equivalent container), both now reading from the ONE shared
 * ChatContext.toolTimeoutFlow instance instead of each calling
 * useToolTimeoutFlow themselves. See that hook's own header comment for the
 * divergence bug this fixes (leaving details on the call panel used to be
 * invisible to the transcript card underneath, open to a second submission
 * once the call ended) — this file no longer owns any state of its own.
 *
 *   OFFER / FAILED  → CallToolTimeoutOffer (new — the two-big-button screen;
 *                      see that file's header for why the call surface
 *                      collapses OFFER/FAILED into one screen)
 *   CHOOSE/MESSAGE/CALLBACK/SUBMITTED_* → the SAME tool-timeout-choose.tsx /
 *                      -message.tsx / -callback.tsx / -submitted.tsx leaves
 *                      text chat uses, unmodified — only the wrapping
 *                      container here is call-surface-specific (via each
 *                      leaf's `className` passthrough, widening past
 *                      StandardCard's default transcript-lane cap so the
 *                      form reads comfortably against the call surface's own
 *                      background — see call-screen.tsx's notice-slot
 *                      width). Forking these leaves was explicitly out of
 *                      scope for this task; this container restyle is the
 *                      sanctioned alternative.
 *   RETRYING / RECOVERED / COLLAPSED → renders nothing. RETRYING lets the
 *                      ordinary call surface (orb + status cluster
 *                      reflecting the LIVE vizMode/turnState) show through
 *                      instead of a bespoke "reaching {tool}…" treatment —
 *                      the orb's own thinking/agent registers already cover
 *                      that beat once retry is in flight, and this task's
 *                      brief only specced the OFFER screen + the capture
 *                      steps, not a third call-specific transitional view
 *                      (see this task's report). RECOVERED is the same call:
 *                      Sara simply speaks the retried answer. COLLAPSED
 *                      (auto-collapse-on-ignore, text chat's ToolTimeoutChip)
 *                      is now reachable here too — the flow is shared and
 *                      that policy applies uniformly regardless of which
 *                      surface is visible (see useToolTimeoutFlow's own
 *                      header) — this panel just renders nothing for it,
 *                      same as RETRYING/RECOVERED, since there is no
 *                      call-surface chip to collapse into.
 *
 * `onDimmedChange` reports whether the orb/status cluster should be showing
 * the shared "held breath" register RIGHT NOW (see call-screen.tsx's own
 * `toolTimeoutDimmed` wiring for why this has to be lifted OUT of this
 * component — CallOrb/CallStatusCluster are call-screen.tsx's direct
 * children, not this panel's). True for every view where the underlying
 * tool call is still unresolved and this panel has the user's attention
 * (OFFER/FAILED/CHOOSE/MESSAGE/CALLBACK); false once a retry is actually in
 * flight (RETRYING — let the normal thinking/agent register take over), it
 * just recovered (RECOVERED), the user's already left their details
 * (SUBMITTED_*), or the incident collapsed (COLLAPSED).
 */
import { useEffect } from 'react';
import { ToolTimeoutAction, ToolTimeoutView } from '@/lib/tool-timeout-view';
import { SupportRequestType } from '@/lib/api/support-requests';
import type { ToolTimeoutFlowContextValue } from '@/hooks/useToolTimeoutFlow';
import { CallToolTimeoutOffer } from './call-tool-timeout-offer';
import { ToolTimeoutChoose } from '../tool-timeout-choose';
import { ToolTimeoutMessage } from '../tool-timeout-message';
import { ToolTimeoutCallback } from '../tool-timeout-callback';
import { ToolTimeoutSubmitted } from '../tool-timeout-submitted';

/** Views where the tool call is still unresolved and this panel owns the
 *  screen — see this file's header comment on `onDimmedChange`. */
const DIMMED_VIEWS: ReadonlySet<ToolTimeoutView> = new Set([
  ToolTimeoutView.OFFER,
  ToolTimeoutView.FAILED,
  ToolTimeoutView.CHOOSE,
  ToolTimeoutView.MESSAGE,
  ToolTimeoutView.CALLBACK,
]);

/** Views for which this panel's switch below actually renders visible
 *  content — the mirror of DIMMED_VIEWS above, but NOT the same set:
 *  DIMMED_VIEWS tracks "still unresolved" (excludes SUBMITTED_*, which
 *  render a confirmation card even though they're done); this one tracks
 *  "produces DOM output" (includes SUBMITTED_*, excludes RETRYING/
 *  RECOVERED/COLLAPSED, which the switch's `default` arm renders `null`
 *  for).
 *
 *  Session-recovery review Finding A: call-screen.tsx used to gate its
 *  shared notice/panel slot on whether `<CallToolTimeoutPanel/>` was a
 *  non-null REACT ELEMENT — true for every view, since this component
 *  itself always returns *something* (the element), even when that
 *  something renders to `null` at the DOM level. So once any tool timed
 *  out, `toolTimeoutFlow` stayed non-null for the rest of the session,
 *  the element-truthiness check stayed true forever, and CallStartNotice/
 *  CallNotice became unreachable — including the mic-permission/failed-
 *  call-start notice from the very start of a session that had already
 *  seen one prior timeout. call-screen.tsx now gates on THIS set instead
 *  of element truthiness (see its own `toolTimeoutPanelVisible`).
 *
 *  Deliberately a POSITIVE list (views that DO render) rather than a
 *  negative one (views that render null): if a future ToolTimeoutView
 *  value is added to the switch below and whoever adds it forgets to
 *  update whichever list exists, the two directions fail differently —
 *  a positive list fails LOUD (the new view's own content silently
 *  vanishes from the call surface, obvious the first time anyone
 *  exercises it); a negative list fails SILENT (this exact bug,
 *  reappearing one view at a time, invisible until a real call happens to
 *  hit it — precisely what evaded every prior test, all of which only
 *  ever exercised OFFER). Loud-on-drift beats silent-on-drift here.
 *  call-tool-timeout-panel.test.tsx has a test that iterates every member
 *  of ToolTimeoutView and checks actual render output against this set,
 *  so the two can't silently drift apart either. */
export const TOOL_TIMEOUT_PANEL_RENDER_VIEWS: ReadonlySet<ToolTimeoutView> =
  new Set([
    ToolTimeoutView.OFFER,
    ToolTimeoutView.FAILED,
    ToolTimeoutView.CHOOSE,
    ToolTimeoutView.MESSAGE,
    ToolTimeoutView.CALLBACK,
    ToolTimeoutView.SUBMITTED_MESSAGE,
    ToolTimeoutView.SUBMITTED_CALLBACK,
  ]);

/** Widens the reused text-chat leaves past StandardCard's default
 *  transcript-lane cap (`max-w-[85%] sm:max-w-[70%]`) so they read
 *  comfortably at the call surface's own notice-slot width (see
 *  call-screen.tsx) — see each leaf's `className` doc comment for why this
 *  is "restyle the container", not "fork the leaf". */
const FORM_LEAF_CLASS_NAME = 'max-w-full';

export interface CallToolTimeoutPanelProps {
  /** ChatContext.toolTimeoutFlow — the ONE shared instance (see this
   *  file's header comment). Callers gate this component's mount on it
   *  being non-null; nothing here re-derives that. */
  flow: ToolTimeoutFlowContextValue;
  onDimmedChange: (dimmed: boolean) => void;
}

export function CallToolTimeoutPanel({
  flow,
  onDimmedChange,
}: CallToolTimeoutPanelProps) {
  const {
    offer,
    phoneOnFile,
    state,
    dispatch,
    submitting,
    error,
    submittedEmail,
    submittedPhone,
    submittedWindow,
    handleRetry,
    handleSubmitMessage,
    handleSubmitCallback,
  } = flow;
  const { retryAvailable } = offer;

  const dimmed = DIMMED_VIEWS.has(state.view);
  useEffect(() => {
    onDimmedChange(dimmed);
    // Clears the held-breath register if this panel unmounts mid-flow (the
    // offer clearing entirely) so a stray true never survives past this
    // incident.
    return () => onDimmedChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimmed]);

  switch (state.view) {
    case ToolTimeoutView.OFFER:
    case ToolTimeoutView.FAILED:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <CallToolTimeoutOffer
            retryAvailable={retryAvailable}
            onRetry={handleRetry}
            onLeaveDetails={() => dispatch(ToolTimeoutAction.LEAVE_DETAILS)}
            // FINDING 5: a failed SEND (socket not open) sets this via the
            // shared hook instead of entering RETRYING — see
            // CallToolTimeoutOffer's own `error` doc comment.
            error={error}
          />
        </div>
      );

    case ToolTimeoutView.CHOOSE:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <ToolTimeoutChoose
            className={FORM_LEAF_CLASS_NAME}
            onSelectMessage={() => dispatch(ToolTimeoutAction.CHOOSE_MESSAGE)}
            onSelectCallback={() => dispatch(ToolTimeoutAction.CHOOSE_CALLBACK)}
            onBack={() => dispatch(ToolTimeoutAction.BACK)}
          />
        </div>
      );

    case ToolTimeoutView.MESSAGE:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <ToolTimeoutMessage
            className={FORM_LEAF_CLASS_NAME}
            onBack={() => dispatch(ToolTimeoutAction.BACK)}
            onSubmit={handleSubmitMessage}
            submitting={submitting}
            error={error}
          />
        </div>
      );

    case ToolTimeoutView.CALLBACK:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <ToolTimeoutCallback
            className={FORM_LEAF_CLASS_NAME}
            phoneOnFile={phoneOnFile}
            onBack={() => dispatch(ToolTimeoutAction.BACK)}
            onSubmit={handleSubmitCallback}
            submitting={submitting}
            error={error}
          />
        </div>
      );

    case ToolTimeoutView.SUBMITTED_MESSAGE:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <ToolTimeoutSubmitted
            className={FORM_LEAF_CLASS_NAME}
            type={SupportRequestType.MESSAGE}
            email={submittedEmail}
          />
        </div>
      );

    case ToolTimeoutView.SUBMITTED_CALLBACK:
      return (
        <div role="status" aria-live="polite" className="w-full">
          <ToolTimeoutSubmitted
            className={FORM_LEAF_CLASS_NAME}
            type={SupportRequestType.CALLBACK}
            phone={submittedPhone}
            callbackWindow={submittedWindow}
          />
        </div>
      );

    // RETRYING / RECOVERED / COLLAPSED — see this file's header comment.
    // These three (and ONLY these three) are absent from
    // TOOL_TIMEOUT_PANEL_RENDER_VIEWS above — if you add a new case here
    // that also returns `null`, add it there too, or call-screen.tsx's
    // notice slot will wrongly think this panel is still showing something
    // (Finding A).
    default:
      return null;
  }
}
